"use client";

import { useEffect, useRef, useState } from "react";
import { drawingComplete } from "@/ui/util";


export function useInstance() {
  const stateRef = useRef<State>(State.Pending);

  const [, setRenderState] = useState<0 | 1>(0);
  const toggleRenderState = () => setRenderState(prev => <0 | 1>(1 - prev));
  const renderingComplete = useRenderingComplete(toggleRenderState);

  useEffect(() => {
    stateRef.current = State.Mounted;

    return () => {
      stateRef.current = State.Unmounted;
    };
  }, []);

  return <const>{
    render() {
      if (stateRef.current === State.Mounted) {
        toggleRenderState();
      }
    },

    /** return a promise that will be resolved when the DOM has been updated */
    renderingComplete() {
      return renderingComplete();
    },

    /** return a promise that will be resolved in the next even loop cycle and an animation frame (less reliable, but in practice it works well) */
    drawingComplete(extraDelayMs?: number) {
      return new Promise<void>(resolve => {
        drawingComplete(extraDelayMs).then(() => {
          if (stateRef.current === State.Mounted) {
            resolve();
          }
        })
      });
    },

    await<T>(promise: Promise<T>) {
      return new Promise<T>((resolve, reject) => {
        promise
          .then(value => (stateRef.current === State.Mounted) && resolve(value))
          .catch(error => (stateRef.current === State.Mounted) && reject(error));
      })
    },

    isAppeared() {
      return stateRef.current !== State.Pending;
    },

    isMounted() {
      return stateRef.current === State.Mounted;
    },

    wasUnmounted() {
      return stateRef.current === State.Unmounted;
    },

    getState() {
      return (
        stateRef.current === State.Pending ? "pending" :
        stateRef.current === State.Mounted ? "mounted" :
        stateRef.current === State.Unmounted ? "unmounted" :
        undefined! as never
      );
    }
  };
}


enum State {
  Pending,
  Mounted,
  Unmounted,
};


function useRenderingComplete(forceRender: () => void) {
  type Resolver = () => void;

  const resolverStackRef = useRef<Resolver[]>([]);

  useEffect(() => {
    if (resolverStackRef.current.length > 0) {
      for (const resolve of resolverStackRef.current) {
        resolve();
      }

      // truncate
      resolverStackRef.current.length = 0;
    }
  });

  return () => new Promise<void>(resolve => {
    const triggerRendering = !resolverStackRef.current.length;
    resolverStackRef.current.push(resolve);

    if (triggerRendering) {
      forceRender();
    }
  });
}
